import type { EditFieldGovernance } from "../types";
import { textLimit } from "@/pages/VendorOnboarding/utils/textLimits";
import {
  isPlausibleAccountNumber,
  isValidGstinFormat,
  isValidIfscFormat,
  isValidPanFormat,
} from "@/services/validators";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function validEmail(v: string): string | null {
  const trimmed = v.trim();
  if (!trimmed) return "Enter an email address.";
  if (!EMAIL_RE.test(trimmed)) return "Enter a valid email address.";
  return null;
}

function validEmailList(v: string): string | null {
  const trimmed = v.trim();
  if (!trimmed) return "Enter at least one email address.";
  const parts = trimmed.split(",").map((s) => s.trim()).filter(Boolean);
  if (parts.length === 0) return "Enter at least one email address.";
  const bad = parts.find((p) => !EMAIL_RE.test(p));
  if (bad) return `"${bad}" is not a valid email address.`;
  return null;
}

function validPhone(v: string): string | null {
  const trimmed = v.trim();
  if (!trimmed) return "Enter a phone number.";
  if (!/^\d{10}$/.test(trimmed)) {
    return "Enter a valid 10-digit phone number.";
  }
  return null;
}

function validPincode(v: string): string | null {
  if (!/^\d{6}$/.test(v.trim())) return "Pincode must be exactly 6 digits.";
  return null;
}

function validAccountNumber(v: string): string | null {
  const trimmed = v.trim();
  if (!trimmed) return "Enter your account number.";
  if (!/^\d+$/.test(trimmed)) return "Account number must contain only digits.";
  if (!isPlausibleAccountNumber(trimmed)) {
    return "Account number must be 6-18 digits.";
  }
  return null;
}

function validIfsc(v: string): string | null {
  const trimmed = v.trim().toUpperCase();
  if (!isValidIfscFormat(trimmed)) {
    return "IFSC must be 11 characters (4 letters + 0 + 6 alphanumeric — e.g. HDFC0001234).";
  }
  return null;
}

function nonEmpty(v: string): string | null {
  if (!v.trim()) return "This field can't be empty.";
  return null;
}

const UDYAM_RE = /^UDYAM-[A-Z]{2}-[0-9]{2}-[0-9]{7}$/;
function validUdyamNumber(v: string): string | null {
  const trimmed = v.trim().toUpperCase();
  if (!trimmed) return null;
  if (!UDYAM_RE.test(trimmed)) {
    return "Must be a valid Udyam registration number (UDYAM-XX-00-0000000).";
  }
  return null;
}

function validGstin(v: string): string | null {
  const trimmed = v.trim().toUpperCase();
  if (!trimmed) return null;
  if (!isValidGstinFormat(trimmed)) {
    return "Must be a valid 15-character GSTIN (e.g. 29AAGCM1234K1Z5).";
  }
  return null;
}

function validPan(v: string): string | null {
  const trimmed = v.trim().toUpperCase();
  if (!trimmed) return null;
  if (!isValidPanFormat(trimmed)) {
    return "Must be a valid 10-character PAN (e.g. ABCDE1234F).";
  }
  return null;
}

const VALIDATORS: Record<string, (v: string) => string | null> = {
  email_id: validEmail,
  po_notification_emails: validEmailList,
  escalation_emails: validEmailList,
  vendor_invoice_portal_access_email: validEmailList,
  tars_production_process_management_access_email: validEmailList,
  poc_contact: validPhone,
  address_billing: nonEmpty,
  location_pincode: validPincode,
  account_number: validAccountNumber,
  ifsc_code: validIfsc,
  msme_number: validUdyamNumber,
  gst: validGstin,
  pan_number: validPan,
};

export const MULTI_VALUE_EMAIL_FIELDS = new Set<string>([
  "po_notification_emails",
  "escalation_emails",
  "vendor_invoice_portal_access_email",
  "tars_production_process_management_access_email",
]);

export function validateFieldValue(
  fieldName: string,
  value: string,
): string | null {
  const validator = VALIDATORS[fieldName];
  if (!validator) return null;
  return validator(value);
}

const ADDRESS_TEXT_FIELDS = new Set<string>([
  "address_billing",
  "address_billing_line_1",
  "address_billing_line_2",
]);

export function textMaxLengthFor(
  fieldName: string,
  governance?: EditFieldGovernance,
): number | undefined {
  const fromBackend = governance?.max_length;
  if (
    typeof fromBackend === "number" &&
    Number.isInteger(fromBackend) &&
    fromBackend > 0
  ) {
    return fromBackend;
  }
  return ADDRESS_TEXT_FIELDS.has(fieldName) ? textLimit("ADDRESS") : undefined;
}
